'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export interface TimedNotification<T> {
    id: number;
    phase: 'enter' | 'exit';
    value: T;
}

interface UseTimedNotificationOptions {
    durationMs: number;
    exitDelayMs: number;
}

interface UseTimedNotificationResult<T> {
    clear: () => void;
    notification: TimedNotification<T> | undefined;
    show: (value: T) => void;
}

export const useTimedNotification = <T>({
    durationMs,
    exitDelayMs,
}: UseTimedNotificationOptions): UseTimedNotificationResult<T> => {
    const [notification, setNotification] = useState<TimedNotification<T> | undefined>();
    const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const idRef = useRef(0);

    const clearTimers = useCallback(() => {
        if (exitTimerRef.current) {
            clearTimeout(exitTimerRef.current);
            exitTimerRef.current = null;
        }

        if (clearTimerRef.current) {
            clearTimeout(clearTimerRef.current);
            clearTimerRef.current = null;
        }
    }, []);

    const clear = useCallback(() => {
        clearTimers();
        setNotification(undefined);
    }, [clearTimers]);

    const show = useCallback(
        (value: T) => {
            clearTimers();

            const id = idRef.current + 1;
            idRef.current = id;
            setNotification({ id, phase: 'enter', value });

            exitTimerRef.current = setTimeout(() => {
                setNotification((current) => (current?.id === id ? { ...current, phase: 'exit' } : current));
                exitTimerRef.current = null;

                clearTimerRef.current = setTimeout(() => {
                    setNotification((current) => (current?.id === id ? undefined : current));
                    clearTimerRef.current = null;
                }, exitDelayMs);
            }, durationMs);
        },
        [clearTimers, durationMs, exitDelayMs],
    );

    useEffect(() => clearTimers, [clearTimers]);

    return { clear, notification, show };
};
